import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Calculator } from "lucide-react";

const links = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF9F6]/80 backdrop-blur-xl border-b border-[#D8D3C8]/50"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-28">
          <a
            href="#top"
            data-testid="nav-logo"
            className="flex items-center group"
          >
            <img
              src="/logo.png"
              alt="Simply Balanced Bookkeeping"
              className="h-20 sm:h-24 w-auto object-contain transition-transform group-hover:scale-[1.03]"
            />
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className="text-sm font-medium text-[#4A4F4D] hover:text-[#4A5D23] transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:w-0 after:bg-[#4A5D23] hover:after:w-full after:transition-all"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://calendly.com/julierankinsbb/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta"
              className="rounded-lg bg-[#4A5D23] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#3A4D13] transition-all duration-300 hover:shadow-lg hover:shadow-[#4A5D23]/20"
            >
              Get a Quote
            </a>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#1A2421]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden bg-[#FAF9F6]/95 backdrop-blur-xl border-b border-[#D8D3C8]/50"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="py-3 text-base font-medium text-[#1A2421] border-b border-[#D8D3C8]/40"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="https://calendly.com/julierankinsbb/30min"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta"
                className="mt-3 rounded-lg bg-[#4A5D23] px-6 py-3 text-center text-sm font-semibold text-white"
              >
                Get a Quote
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
